import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import {
  type BudgetAssessmentDto,
  type BudgetAssessmentState,
  getCurrentBudgetAssessmentFromConnect,
} from '@/lib/connect-api';

export interface BudgetAssessmentRead {
  /** What to do next, resolved server-side. Never re-derived from `valid_until`. */
  state: BudgetAssessmentState;
  assessment: BudgetAssessmentDto | null;
  /** The last server response is the only source of answers and totals, so every
   *  write hands its result back through here rather than the page keeping a copy. */
  setAssessment: (assessment: BudgetAssessmentDto) => void;
  isLoading: boolean;
  /** True only for a failed read. The flow being switched off is `not_required`. */
  hasError: boolean;
  refetch: () => void;
}

/**
 * Reads whether this customer needs, has, or is part-way through an assessment.
 *
 * Anything that means "we cannot ask" — the feature off for this operator, preview
 * mode, no account context, a Stage-2 session (which the API answers `not_required`
 * for anyway) — collapses to `not_required`, so callers render nothing rather than
 * offer a flow that would dead-end.
 */
export function useBudgetAssessment(): BudgetAssessmentRead {
  const { accountId, authStage, getAccessToken } = useAuth();
  const { useMockData } = useConfiguration();
  const [state, setState] = useState<BudgetAssessmentState>('not_required');
  const [assessment, setAssessment] = useState<BudgetAssessmentDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    // Preview and demo serve mock data with no auth at all; a Connect read here
    // would throw on the missing credential rather than render anything useful.
    if (useMockData || !accountId || (authStage !== 'limited' && authStage !== 'full')) {
      setState('not_required');
      setAssessment(null);
      setHasError(false);
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    getCurrentBudgetAssessmentFromConnect(authStage, getAccessToken)
      .then((result) => {
        if (!active) return;
        setState(result.state);
        setAssessment(result.assessment);
        setHasError(false);
      })
      .catch(() => {
        if (!active) return;
        setState('not_required');
        setAssessment(null);
        setHasError(true);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [useMockData, accountId, authStage, getAccessToken, reloads]);

  return { state, assessment, setAssessment, isLoading, hasError, refetch };
}
